import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useRest } from "@/composables/useRest";

export const useArticulosStore = defineStore("articulos", () => {
  const articulos = ref([]);
  const allArticulos = ref([]);
  const allArticulosLoaded = ref(false);
  const catalogNeedsRefresh = ref(false);
  const isLoading = ref(false);
  const pageSize = ref(6);
  const currentPage = ref(1);
  const totalArticulos = ref(0);
  const activeSearchQuery = ref(null);

  const rest = useRest();

  const offset = computed(() => (currentPage.value - 1) * pageSize.value);
  const totalPages = computed(() => Math.ceil(totalArticulos.value / pageSize.value));

  async function getTotal(searchQuery = null) {
    try {
      let url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/articulos/?getTotal=true`;
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      const { data } = await rest.get({ url });
      totalArticulos.value = +data.doc.total;
      return totalArticulos.value;
    } catch (e) {
      console.error("getTotal>>", e);
      return totalArticulos.value;
    }
  }

  async function setAllArticulos(refresh = false) {
    try {
      const shouldRefresh = refresh || catalogNeedsRefresh.value;
      if (allArticulosLoaded.value && !shouldRefresh) {
        articulos.value = allArticulos.value;
        return allArticulos.value;
      }

      const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/articulos`;
      const r = await rest.get({ url });
      const datos = r.data.data;

      allArticulos.value = datos;
      allArticulosLoaded.value = true;
      catalogNeedsRefresh.value = false;
      articulos.value = datos;

      return allArticulos.value;
    } catch (e) {
      console.error("setAllArticulos>>", e);
      return allArticulos.value;
    }
  }

  async function setArticulos(page = 1) {
    try {
      isLoading.value = true;
      activeSearchQuery.value = null;
      currentPage.value = Math.max(1, Number(page) || 1);

      const limit = pageSize.value;
      const offsetValue = (currentPage.value - 1) * limit;

      const url =
        `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/articulos/` +
        `?limit=${limit}&offset=${offsetValue}`;
      const r = await rest.get({ url });

      articulos.value = r.data.data;
      return articulos.value;
    } catch (e) {
      console.error("setArticulos>>", e);
      return articulos.value;
    } finally {
      isLoading.value = false;
    }
  }

  async function searchArticulos(searchQuery, page = 1) {
    try {
      isLoading.value = true;
      activeSearchQuery.value = searchQuery || null;
      currentPage.value = Math.max(1, Number(page) || 1);

      const limit = pageSize.value;
      const offsetValue = (currentPage.value - 1) * limit;

      let url =
        `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/articulos/` +
        `?limit=${limit}&offset=${offsetValue}`;

      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }

      const r = await rest.get({ url });
      articulos.value = r.data.data;
      return articulos.value;
    } catch (e) {
      console.error("searchArticulos>>", e);
      return articulos.value;
    } finally {
      isLoading.value = false;
    }
  }

  async function createArticulo(payload) {
    try {
      const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/articulos`;
      const { data } = await rest.post({ url, formData: payload });
      allArticulosLoaded.value = false;
      catalogNeedsRefresh.value = true;
      return data;
    } catch (e) {
      console.error("createArticulo>>", e);
      throw e;
    }
  }

  async function updateArticulo(id, payload) {
    try {
      const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/articulos/${id}`;
      const { data } = await rest.put({ url, formData: payload });
      allArticulosLoaded.value = false;
      catalogNeedsRefresh.value = true;
      return data;
    } catch (e) {
      console.error("updateArticulo>>", e);
      throw e;
    }
  }

  async function deleteArticulo(id) {
    try {
      const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/articulos/${id}`;
      const { data } = await rest.del({ url });

      allArticulosLoaded.value = false;
      catalogNeedsRefresh.value = true;

      await getTotal(activeSearchQuery.value);

      const maxPage = Math.max(1, totalPages.value || 1);
      const pageToLoad = Math.min(currentPage.value, maxPage);

      if (activeSearchQuery.value) {
        await searchArticulos(activeSearchQuery.value, pageToLoad);
      } else {
        await setArticulos(pageToLoad);
      }

      return data;
    } catch (e) {
      console.error("deleteArticulo>>", e);
      throw e;
    }
  }

  function handlePageChange(event) {
    const first = Number(event?.first || 0);
    const page = Math.floor(first / pageSize.value) + 1;
    goToPage(page);
  }

  function goToPage(page) {
    const nextPage = Math.max(1, Number(page) || 1);

    if (activeSearchQuery.value) {
      searchArticulos(activeSearchQuery.value, nextPage);
      return;
    }

    setArticulos(nextPage);
  }

  function prevPage() {
    if (currentPage.value > 1) {
      goToPage(currentPage.value - 1);
    }
  }

  function nextPage() {
    if (currentPage.value < totalPages.value) {
      goToPage(currentPage.value + 1);
    }
  }

  return {
    articulos,
    allArticulos,
    isLoading,
    pageSize,
    currentPage,
    totalArticulos,
    offset,
    totalPages,
    getTotal,
    setAllArticulos,
    setArticulos,
    searchArticulos,
    createArticulo,
    updateArticulo,
    deleteArticulo,
    handlePageChange,
    goToPage,
    prevPage,
    nextPage,
  };
});
